import React from 'react';
import PropTypes from 'prop-types';
import { AppContext } from '@prisjakt/ui-app-context';
import { injectScript } from '../../helpers';
import { getUpdates, isLoaded, get } from './helpers';
import { findPlacement } from '../../helpers/placement';
import AdContextType from '../../context/type';

class AppNexusProvider extends React.PureComponent {
  constructor(props) {
    super(props);
    this.definedTags = [];
    this.loadedTags = [];
    this.apntag = { anq: [] };
    this.log = props.adContext.actions.log;
    this.uniqueIdCounter = 0;
    this.state = {
      context: {
        actions: {
          reload: flush => this.reload(flush),
          queue: action => this.queue(action),
          defineTag: (placement, asyncTag) =>
            this.defineTag(placement, asyncTag),
          findPlacement: placement => this.findPlacement(placement),
          getPlacementOptions: placement => this.getPlacementOptions(placement),
        },
      },
    };
    this.abortIdle = null;
  }

  componentDidMount() {
    if (window && window.requestIdleCallback) {
      const idleHandler = window.requestIdleCallback(this.init.bind(this), {
        timeout: 10000,
      });
      this.abortIdle = () => {
        window.cancelIdleCallback(idleHandler);
        this.abortIdle = null;
      };
    } else {
      this.init();
    }
  }

  componentWillUnmount() {
    if (this.abortIdle) {
      this.abortIdle();
    }

    this.queue(apntag => apntag.offEvent('adLoaded'));
    this.flush();
  }

  init() {
    injectScript(this.props.config.lib, isLoaded)
      .then(() => {
        this.log('[AppNexus] LIB LOADED');
        const queue = this.apntag.anq;
        this.apntag = get();
        queue.forEach(item => this.apntag.anq.push(item));
        this.reload(false);
      })
      .catch(() => {
        if (window.dataLayer) {
          window.dataLayer.push({ adblock: 'active' });
        }
      });
    this.queue(apntag => {
      apntag.onEvent('adLoaded', obj => {
        const panoramaTopElm = document.querySelector('*[id^=ad_panorama_top]');
        this.loadedTags.push(obj);
        this.setState(prevState => ({
          ...prevState,
          context: {
            ...prevState.context,
            takeover: panoramaTopElm && panoramaTopElm.clientWidth > 980,
          },
        }));
      });
    });
  }

  queue(action) {
    this.apntag.anq.push(() => action(get()));
  }

  flush() {
    this.log('[AppNexus] FLUSH');
    this.queue(apntag => apntag.clearRequest());
    this.uniqueIdCounter = this.uniqueIdCounter % 10000;
  }

  reload(flush = true) {
    // Use flush to reload all ads on page
    if (flush) {
      this.flush();
    }
    // Use this hack to ensure AdContainers are rendered/updated
    window.requestAnimationFrame(() => {
      this.updatePageOpts();
      this.loadTags();
    });
  }

  updatePageOpts() {
    this.queue(apntag => apntag.setPageOpts(this.getPageOpts()));
  }

  getPageOpts() {
    const {
      market,
      state: {
        siteMode,
        siteName,
        pageType,
        pathChain,
        brand,
        priceData,
        schPageType,
      },
    } = this.props.adContext;
    const { pageOpts } = this.props.config;
    const { location, screen, innerHeight, innerWidth } = window;
    const adTestMode = location.search
      ? location.search.indexOf('adtest=1') > -1
      : false;
    const test = adTestMode ? '-Test' : '';
    const publisher = `${market.toUpperCase()}-${siteName}${test}`;
    const supplyType = siteMode === 'desktop' ? 'web_desktop' : 'web_phone';
    const pagegen = ['mobile', 'desktop'].includes(this.context.renderMode)
      ? 'classic'
      : 'rfe';
    const { ads } = this.context.market;
    if (ads && ads.memberIdOverride) {
      pageOpts.member = ads.memberIdOverride;
    }
    return {
      ...pageOpts,
      keywords: {
        ...pageOpts.keywords,
        'aa-sch-page_type': schPageType,
        'aa-sch-supply_type': supplyType,
        'aa-sch-country_code': adTestMode ? 'te' : market,
        'aa-sch-publisher': publisher,
        'se-generic-pagegen': pagegen,
        'se-generic-appmode': 'notinapp',
        'se-generic-screenheight': screen.height,
        'se-generic-screenwidth': screen.width,
        'se-generic-viewportheight': innerHeight,
        'se-generic-viewportwidth': innerWidth,
        'se-generic-sitemode': siteMode,
        'se-generic-page': pageType,
        'se-generic-price': priceData,
        'se-prisjakt-section': pathChain,
        ...(brand &&
          brand.name && { 'se-generic-brand': brand.name.toLowerCase() }),
      },
    };
  }

  /**
   * A counter to give each ad placement a unique (HTML element) ID.
   */
  getUniqueId() {
    return this.uniqueIdCounter++; // eslint-disable-line no-plusplus
  }

  findPlacement(placement) {
    const { state } = this.props.adContext;
    const key = findPlacement(
      state.placementsMap,
      placement,
      state.pathChain,
      state.pageType,
      state.siteMode
    );
    this.log('[AppNexus] LOOKING FOR PLACEMENT', placement, key);
    const placementOptions = key
      ? {
          ...state.placementsMap[key],
          ...this.getPlacementOptions(placement),
          key,
          containerId: `${
            state.placementsMap[key].containerId
          }-${this.getUniqueId()}`,
        }
      : null;
    return placementOptions;
  }

  getPlacementOptions(placement) {
    const { placementOptions } = this.props.config;
    return placementOptions && placementOptions[placement]
      ? placementOptions[placement]
      : {};
  }

  defineTag(placement, asyncTag = false) {
    this.log('[AppNexus] DEFINE TAG', placement);
    this.definedTags.push(placement);

    if (asyncTag) {
      this.loadTags();
    }
  }

  loadTags() {
    this.queue(apntag => {
      this.log('[AppNexus] LOAD TAGS', this.definedTags);
      this.definedTags.forEach(tag => {
        const { containerId, placementCode, sizes } = tag;
        apntag.defineTag({
          invCode: placementCode,
          targetId: containerId,
          sizes,
        });
      });
      apntag.loadTags();
      this.definedTags = [];
      this.loadedTags = [];
    });
  }

  componentDidUpdate(prevProps) {
    const updates = getUpdates(
      prevProps.adContext.state,
      this.props.adContext.state,
      ['pathKey', 'pageType', 'siteMode', 'view']
    );
    if (Object.values(updates).some(item => !!item)) {
      const onlyViewUpdated = Object.keys(updates).every(
        key => !updates[key] || (key === 'view' && updates.view)
      );
      this.reload(!onlyViewUpdated);
    }
  }

  render() {
    const BaseProvider = this.props.baseProvider;
    return (
      <BaseProvider value={this.state.context}>
        {this.props.children}
      </BaseProvider>
    );
  }
}

const { element, object, func, shape } = PropTypes;

AppNexusProvider.propTypes = {
  adContext: AdContextType.isRequired,
  baseProvider: object.isRequired, // eslint-disable-line react/forbid-prop-types
  config: object.isRequired, // eslint-disable-line react/forbid-prop-types
  children: element.isRequired,
};

AppNexusProvider.contextType = AppContext;

export const AppNexusContextType = shape({
  actions: shape({
    queue: func,
    defineTag: func,
    findPlacement: func,
    getPlacementOptions: func,
  }),
});

export default AppNexusProvider;
